import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, Menu, X } from "lucide-react";
import { AuthContext } from "../context/AuthContext";
import NotificationsBell from "./NotificationsBell";

const MedimagicIcon = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
    <path d="M40 6 L74 40 L40 74 L6 40 Z" stroke="#3072FF" strokeWidth="5.5" fill="none" strokeLinejoin="round" />
    <polyline points="12,40 25,40 31,20 40,60 49,40 62,40 68,40" stroke="#43E0FF" strokeWidth="3.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Build the menu for a portal user based on their role + the access matrix.
// Each portal role has its own menu shape; everything else is gated by the
// access matrix in /access.js.
function buildMenu(role) {
  if (!role) return [];

  const items = [{ label: "Dashboard", path: "/users/dashboard", moduleKey: "dashboard" }];

  if (role === "it_admin") {
    items.push({
      label: "Users",
      moduleKey: "users",
      children: [{ label: "Faculty", path: "/users/faculty" }],
    });
    items.push({ label: "Calendar",  path: "/users/calendar",  moduleKey: "timetable" });
    items.push({ label: "Timetable", path: "/users/timetable", moduleKey: "timetable" });
    items.push({ label: "Tickets",   path: "/users/tickets",   moduleKey: "tickets"   });
    items.push({ label: "Contact",   path: "/users/contact",   moduleKey: "contact"   });
  } else if (role === "college_admin") {
    items.push({ label: "Users",     path: "/users/users",     moduleKey: "users"     });
    items.push({ label: "Timetable", path: "/users/timetable", moduleKey: "timetable" });
    items.push({ label: "Tickets",   path: "/users/tickets",   moduleKey: "tickets"   });
    items.push({ label: "Contact",   path: "/users/contact",   moduleKey: "contact"   });
  } else if (role === "faculty") {
    items.push({ label: "Calendar",  path: "/users/calendar",  moduleKey: "calendar"  });
    items.push({ label: "Tickets",   path: "/users/tickets",   moduleKey: "tickets"   });
    items.push({ label: "Contact",   path: "/users/contact",   moduleKey: "contact"   });
  } else if (role === "support_user") {
    items.push({ label: "Timetable", path: "/users/timetable", moduleKey: "timetable" });
    items.push({ label: "Tickets",   path: "/users/tickets",   moduleKey: "tickets"   });
    items.push({ label: "Contact",   path: "/users/contact",   moduleKey: "contact"   });
  } else if (role === "state_admin" || role === "university_admin") {
    items.push({ label: "Tickets",   path: "/users/tickets",   moduleKey: "tickets"   });
    items.push({ label: "Contact",   path: "/users/contact",   moduleKey: "contact"   });
  }

  return items;
}

export default function UserTopNav() {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const dropdownRef = useRef(null);

  const menu = useMemo(() => buildMenu(user?.role), [user?.role]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenMenu(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setOpenMenu(null);
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const isParentActive = (children) =>
    children.some((c) => location.pathname === c.path || location.pathname.startsWith(c.path + "/"));

  const handleLogout = async () => {
    await logout();
    navigate("/users/login");
  };

  return (
    <>
    <header className="topnav">
      <div className="topnav-inner">
        <Link to="/users/dashboard" className="topnav-logo">
          <MedimagicIcon size={38} />
          <div className="topnav-logo-text">
            <span className="topnav-logo-name">medimagic</span>
            <span className="topnav-logo-sub">dashboard</span>
          </div>
        </Link>

        {/* Desktop nav links */}
        <nav className="topnav-links">
          {menu.map((item) => {
            if (item.children) {
              const open = openMenu === item.label;
              return (
                <div
                  key={item.label}
                  className="topnav-dropdown-wrap"
                  ref={open ? dropdownRef : null}
                >
                  <button
                    className={`topnav-link topnav-link-btn${isParentActive(item.children) ? " active" : ""}`}
                    onClick={() => setOpenMenu(open ? null : item.label)}
                    data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}-menu`}
                  >
                    {item.label}
                    <ChevronDown size={14} className={`topnav-chevron${open ? " open" : ""}`} />
                  </button>

                  {open && (
                    <div className="topnav-dropdown">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`topnav-dropdown-item${isActive(child.path) ? " active" : ""}`}
                          data-testid={`nav-${child.label.toLowerCase().replace(/\s+/g, "-")}`}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            return (
              <Link
                key={item.path}
                to={item.path}
                className={`topnav-link${isActive(item.path) ? " active" : ""}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="topnav-user">
          <NotificationsBell to="/users/notifications" />
          <div className="topnav-avatar">
            {(user?.name || user?.email || "U")[0].toUpperCase()}
          </div>
          <span className="topnav-username">
            {user?.name || user?.email}
          </span>
          <button
            className="topnav-logout"
            onClick={handleLogout}
            data-testid="logout-btn"
            title="Log out"
          >
            <LogOut size={16} />
          </button>
        </div>

        {/* Hamburger — mobile only */}
        <button
          className="topnav-hamburger"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="topnav-hamburger"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
    </header>

      {/* Mobile slide-down menu — sibling outside <header> */}
      {mobileOpen && (
        <nav className="topnav-mobile-menu" data-testid="topnav-mobile-menu">
          {menu.map((item) => {
            if (item.children) {
              const open = openMenu === item.label;
              return (
                <div key={item.label}>
                  <button
                    className={`topnav-mobile-link topnav-mobile-parent${isParentActive(item.children) ? " active" : ""}`}
                    onClick={() => setOpenMenu(open ? null : item.label)}
                  >
                    {item.label}
                    <ChevronDown size={14} className={`topnav-chevron${open ? " open" : ""}`} />
                  </button>
                  {open && (
                    <div className="topnav-mobile-sub">
                      {item.children.map((child) => (
                        <Link
                          key={child.path}
                          to={child.path}
                          className={`topnav-mobile-sublink${isActive(child.path) ? " active" : ""}`}
                          onClick={() => setMobileOpen(false)}
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`topnav-mobile-link${isActive(item.path) ? " active" : ""}`}
                onClick={() => setMobileOpen(false)}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>
      )}
    </>
  );
}
